import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { KINETIC_WORD_SETTING } from './kineticWordSetting';
import { check, repeatsAClass, valuesOf } from '../skillUsage';

export const glyphOvershootPopSkill: CodeoSkill = {
    name: SKILL_NAMES.glyphOvershootPop,
    kind: 'codeo',
    description:
        'Pop a word in letter by letter from nothing, each glyph swelling far past its size — so ' +
        'neighbours crash into each other — then snapping back under and settling: for a word ' +
        'about bursting, surprise, overshooting, too much, or excitement.',
    checkLooksFor:
        'per-letter spans scaled up from 0 to past 1.3, then back below 1 before they settle',
    documentUsesSkill: check((script, markup) => {
        const scales = valuesOf(script, 'scale');
        return (
            repeatsAClass(markup, 'span') &&
            scales.includes(0) &&
            scales.some((value) => value >= 1.3) &&
            scales.some((value) => value > 0 && value < 1)
        );
    }),
    reviewNote:
        'Letters overlapping their neighbours, or drawn much larger than the rest of the word, are ' +
        'this technique working at the peak of its overshoot.',
    instructions: `# Glyph Overshoot Pop

Each letter **pops out of nothing at its own centre**, swells to half again its size — big enough that it **crashes over its neighbours** — then rebounds to three-quarters, and settles at full size. The letters go a frame or so apart, left to right, so the word blooms across the frame in a quarter of a second and wobbles for half a second more. It is overshoot you can see: the word does too much, then corrects.

Use it for a word about bursting, popping, surprise, overshoot, excess, celebration, "wow". As a default entrance it is cartoonish.

${KINETIC_WORD_SETTING}

## Markup

Capitals, sized to the frame by font-size (\`data-l5-allow-literal\`), letters as \`inline-block\` spans. Accent one or two letters (the vowels, or the letters that carry the word's sound) in the palette's accent colour so the collisions read.

\`\`\`html
<div id="pop-beat" class="absolute inset-0 flex items-center justify-center">
  <div id="pop-word" data-l5-allow-literal style="line-height: 1; white-space: nowrap; font-family: var(--l5-font-0), sans-serif; font-weight: 800; text-transform: uppercase; color: var(--l5-color-3, #fafafa);">
    <span class="op-ch" style="color: var(--l5-color-1, #ff5a36);">W</span><span class="op-ch">O</span><span class="op-ch" style="color: var(--l5-color-1, #ff5a36);">W</span>
  </div>
</div>
\`\`\`

\`\`\`css
.op-ch { display: inline-block; transform-origin: 50% 55%; }
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const word = document.querySelector('#pop-word');
fitWord(word, 0.8);

const CUE = 0.4;
const letters = [...word.querySelectorAll('.op-ch')];
letters.forEach((ch, i) => {
  const at = CUE - 0.1 + i * 0.012;
  tl.fromTo(ch, { scale: 0 }, { scale: 1.5, duration: 0.16, ease: 'power2.out' }, at);
  tl.to(ch, { scale: 0.75, duration: 0.09, ease: 'sine.inOut' }, at + 0.16);
  tl.to(ch, { scale: 1.08, duration: 0.09, ease: 'sine.inOut' }, at + 0.25);
  tl.to(ch, { scale: 1, duration: 0.12, ease: 'sine.out' }, at + 0.34);
});

tl.set({}, {}, 1.6);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Two lines.** The second line starts its pops when the first line's third letter peaks, so the two blooms overlap.
- **Bigger burst.** Push the peak to 1.8 for the payoff word only. Above that the letters stop reading as letters.
- **Loose letters.** Give each letter a small random rotation at the peak (±6°) that returns to 0 at the settle, for a looser, rubbery feel.

## Pitfalls

- **Overlap is the point.** At the peak, letters cover their neighbours. Don't space the letters out to avoid it, or the overshoot has nothing to crash into.
- **From the centre.** Scaled from a corner, a letter flies in instead of popping.
- **Short.** The whole pop and settle is about half a second. Stretched longer, it reads as floating.
- The letters are transform-tweened, so they carry no CSS \`transform\` beyond the origin.`,
};
